import React, { useState, useEffect } from "react";
import { Patient } from "../../PatientCard";
import VitalSignsService, {
  CreateVitalSignsData,
  PatientVitalSigns,
} from "../../../../services/vitalService";
import getIcon from "../../../../utils/Icons";

interface VitalSignsModalProps {
  patient: Patient;
  isOpen: boolean;
  onClose: () => void;
  onVitalSignsRecorded?: (vitalSignsId: number) => void;
  appointmentId?: number;
  editingVitalSigns?: PatientVitalSigns | null;
}

const VitalSignsModal: React.FC<VitalSignsModalProps> = ({
  patient,
  isOpen,
  onClose,
  onVitalSignsRecorded,
  appointmentId,
  editingVitalSigns,
}) => {
  const [formData, setFormData] = useState<CreateVitalSignsData>({
    patient_id: patient.id,
    appointment_id: appointmentId,
    systolic_bp: undefined,
    diastolic_bp: undefined,
    heart_rate: undefined,
    respiratory_rate: undefined,
    temperature: undefined,
    oxygen_saturation: undefined,
    weight: undefined,
    height: undefined,
    pain_scale: undefined,
    notes: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const isEditMode = !!editingVitalSigns;

  // Load data when editing or clear when creating new
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        patient_id: patient.id,
        appointment_id: appointmentId,
        systolic_bp: undefined,
        diastolic_bp: undefined,
        heart_rate: undefined,
        respiratory_rate: undefined,
        temperature: undefined,
        oxygen_saturation: undefined,
        weight: undefined,
        height: undefined,
        pain_scale: undefined,
        notes: "",
      });
      setErrors({});
    } else if (editingVitalSigns) {
      // Load existing data for editing
      setFormData({
        patient_id: patient.id,
        appointment_id: editingVitalSigns.appointment_id,
        systolic_bp: editingVitalSigns.vital_signs?.systolic_bp,
        diastolic_bp: editingVitalSigns.vital_signs?.diastolic_bp,
        heart_rate: editingVitalSigns.vital_signs?.heart_rate,
        respiratory_rate: editingVitalSigns.vital_signs?.respiratory_rate,
        temperature: editingVitalSigns.vital_signs?.temperature
          ? Number(editingVitalSigns.vital_signs.temperature)
          : undefined,
        oxygen_saturation: editingVitalSigns.vital_signs?.oxygen_saturation,
        weight: editingVitalSigns.vital_signs?.weight
          ? Number(editingVitalSigns.vital_signs.weight)
          : undefined,
        height: editingVitalSigns.vital_signs?.height
          ? Number(editingVitalSigns.vital_signs.height)
          : undefined,
        pain_scale: editingVitalSigns.vital_signs?.pain_scale,
        notes: editingVitalSigns.notes || "",
      });
    }
  }, [isOpen, patient.id, appointmentId, editingVitalSigns]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleInputChange = (field: keyof CreateVitalSignsData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value === "" ? undefined : value,
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Check that at least one vital sign is provided
    const vitalSigns = [
      "systolic_bp",
      "diastolic_bp",
      "heart_rate",
      "respiratory_rate",
      "temperature",
      "oxygen_saturation",
      "weight",
      "height",
      "pain_scale",
    ];

    const hasVitalSigns = vitalSigns.some(
      (field) =>
        formData[field as keyof CreateVitalSignsData] !== undefined &&
        formData[field as keyof CreateVitalSignsData] !== null &&
        formData[field as keyof CreateVitalSignsData] !== "",
    );

    if (!hasVitalSigns) {
      newErrors.general = "Please record at least one vital sign measurement";
    }

    // Validate blood pressure relationship
    if (formData.systolic_bp && formData.diastolic_bp) {
      if (formData.systolic_bp <= formData.diastolic_bp) {
        newErrors.systolic_bp =
          "Systolic pressure must be higher than diastolic pressure";
      }
    }

    // Validate ranges
    if (
      formData.systolic_bp &&
      (formData.systolic_bp < 50 || formData.systolic_bp > 300)
    ) {
      newErrors.systolic_bp = "Systolic BP must be between 50-300 mmHg";
    }

    if (
      formData.diastolic_bp &&
      (formData.diastolic_bp < 30 || formData.diastolic_bp > 200)
    ) {
      newErrors.diastolic_bp = "Diastolic BP must be between 30-200 mmHg";
    }

    if (
      formData.heart_rate &&
      (formData.heart_rate < 30 || formData.heart_rate > 250)
    ) {
      newErrors.heart_rate = "Heart rate must be between 30-250 bpm";
    }

    if (
      formData.respiratory_rate &&
      (formData.respiratory_rate < 5 || formData.respiratory_rate > 60)
    ) {
      newErrors.respiratory_rate =
        "Respiratory rate must be between 5-60 breaths/min";
    }

    if (
      formData.temperature &&
      (formData.temperature < 30 || formData.temperature > 45)
    ) {
      newErrors.temperature = "Temperature must be between 30-45°C";
    }

    if (
      formData.oxygen_saturation &&
      (formData.oxygen_saturation < 50 || formData.oxygen_saturation > 100)
    ) {
      newErrors.oxygen_saturation = "Oxygen saturation must be between 50-100%";
    }

    if (formData.weight && (formData.weight < 0.5 || formData.weight > 500)) {
      newErrors.weight = "Weight must be between 0.5-500 kg";
    }

    if (formData.height && (formData.height < 30 || formData.height > 250)) {
      newErrors.height = "Height must be between 30-250 cm";
    }

    if (
      formData.pain_scale !== undefined &&
      (formData.pain_scale < 0 || formData.pain_scale > 10)
    ) {
      newErrors.pain_scale = "Pain scale must be between 0-10";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      let vitalSigns;

      if (isEditMode && editingVitalSigns?.id) {
        // Update existing record
        vitalSigns = await VitalSignsService.updateVitalSigns(
          editingVitalSigns.id,
          formData,
        );
      } else {
        // Create new record
        vitalSigns = await VitalSignsService.createVitalSigns(formData);
      }

      if (onVitalSignsRecorded) {
        onVitalSignsRecorded(vitalSigns.id!);
      }

      onClose();
    } catch (error: any) {
      setErrors({
        submit:
          error.message ||
          `Failed to ${isEditMode ? "update" : "record"} vital signs`,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    zIndex: 1000,
    display: "flex",
    alignItems: isMobile ? "flex-start" : "center",
    justifyContent: "center",
    padding: isMobile ? "10px" : "20px",
    overflow: "auto",
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: "#ffffff",
    borderRadius: isMobile ? "12px" : "16px",
    width: "100%",
    maxWidth: isMobile ? "100%" : "800px",
    maxHeight: isMobile ? "100%" : "90vh",
    overflow: "hidden",
    boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
    display: "flex",
    flexDirection: "column",
    margin: isMobile ? "10px 0" : "auto",
  };

  const headerStyle: React.CSSProperties = {
    padding: isMobile ? "16px" : isTablet ? "18px" : "20px",
    borderBottom: "1px solid #e5e7eb",
    backgroundColor: "#f8fafc",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: isMobile ? "12px" : isTablet ? "14px" : "16px",
    flexShrink: 0,
  };

  const contentStyle: React.CSSProperties = {
    flex: 1,
    overflow: "auto",
    padding: isMobile ? "16px" : "24px",
  };

  const footerStyle: React.CSSProperties = {
    padding: "20px",
    borderTop: "1px solid #e5e7eb",
    backgroundColor: "#f8fafc",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: isMobile ? "column" : "row",
    gap: isMobile ? "16px" : "0",
    flexShrink: 0,
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: "24px",
  };

  const inputGroupStyle: React.CSSProperties = {
    marginBottom: "16px",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "14px",
    fontWeight: "500",
    marginBottom: "6px",
    color: "#374151",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "14px",
    transition: "all 0.2s ease",
    fontFamily: "inherit",
    boxSizing: "border-box",
  };

  const errorInputStyle: React.CSSProperties = {
    ...inputStyle,
    border: "1px solid #ef4444",
  };

  const buttonStyle = (
    variant: "primary" | "secondary" = "primary",
  ): React.CSSProperties => ({
    padding: "12px 24px",
    borderRadius: "8px",
    border: "none",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: variant === "primary" ? "#3b82f6" : "#e5e7eb",
    color: variant === "primary" ? "white" : "#374151",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    minWidth: "120px",
  });

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ flex: 1 }}>
            <h2
              style={{
                fontSize: isMobile ? "16px" : isTablet ? "18px" : "20px",
                fontWeight: "700",
                margin: 0,
                color: "#1f2937",
                flex: 1,
              }}
            >
              {isEditMode ? "✏️ Edit Vital Signs" : "🩺 Record Vital Signs"}
            </h2>
            <p
              style={{
                fontSize: "14px",
                color: "#6b7280",
                margin: "4px 0 0 0",
              }}
            >
              {patient.full_name ||
                `${patient.first_name} ${patient.last_name}`}
            </p>
          </div>
          <button
            id="close_modal_button"
            name="close_modal_button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: "#6b7280",
              padding: "0",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "6px",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={contentStyle}>
          {errors.general && (
            <div
              style={{
                color: "#ef4444",
                fontSize: "14px",
                marginBottom: "16px",
                padding: "12px",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "6px",
              }}
            >
              {errors.general}
            </div>
          )}

          {/* Blood Pressure */}
          <div style={sectionStyle}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                marginBottom: "16px",
                color: "#1f2937",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              ❤️ Blood Pressure
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : isTablet
                    ? "1fr 1fr"
                    : "1fr 1fr",
                gap: "16px",
              }}
            >
              <div style={inputGroupStyle}>
                <label style={labelStyle} htmlFor="systolic_bp">
                  Systolic (mmHg)
                </label>
                <input
                  type="number"
                  id="systolic_bp"
                  name="systolic_bp"
                  value={formData.systolic_bp || ""}
                  onChange={(e) =>
                    handleInputChange(
                      "systolic_bp",
                      parseInt(e.target.value) || undefined,
                    )
                  }
                  placeholder="e.g., 120"
                  min="50"
                  max="300"
                  style={errors.systolic_bp ? errorInputStyle : inputStyle}
                />
                {errors.systolic_bp && (
                  <div
                    style={{
                      color: "#ef4444",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {errors.systolic_bp}
                  </div>
                )}
              </div>

              <div style={inputGroupStyle}>
                <label style={labelStyle} htmlFor="diastolic_bp">
                  Diastolic (mmHg)
                </label>
                <input
                  type="number"
                  id="diastolic_bp"
                  name="diastolic_bp"
                  value={formData.diastolic_bp || ""}
                  onChange={(e) =>
                    handleInputChange(
                      "diastolic_bp",
                      parseInt(e.target.value) || undefined,
                    )
                  }
                  placeholder="e.g., 80"
                  min="30"
                  max="200"
                  style={errors.diastolic_bp ? errorInputStyle : inputStyle}
                />
                {errors.diastolic_bp && (
                  <div
                    style={{
                      color: "#ef4444",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {errors.diastolic_bp}
                  </div>
                )}
              </div>
            </div>

            {/* Blood Pressure Display */}
            {formData.systolic_bp && formData.diastolic_bp && (
              <div
                style={{
                  marginTop: "12px",
                  padding: "8px 12px",
                  background: "#f0f9ff",
                  border: "1px solid #dbeafe",
                  borderRadius: "6px",
                  fontSize: "13px",
                  color: "#1e40af",
                }}
              >
                <strong>Blood Pressure:</strong> {formData.systolic_bp}/
                {formData.diastolic_bp} mmHg
              </div>
            )}
          </div>

          {/* Heart Rate & Respiratory Rate */}
          <div style={sectionStyle}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                marginBottom: "16px",
                color: "#1f2937",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              💓 Heart & Respiratory Rate
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : isTablet
                    ? "1fr 1fr"
                    : "1fr 1fr",
                gap: "16px",
              }}
            >
              <div style={inputGroupStyle}>
                <label style={labelStyle} htmlFor="heart_rate">
                  Heart Rate (bpm)
                </label>
                <input
                  type="number"
                  id="heart_rate"
                  name="heart_rate"
                  value={formData.heart_rate || ""}
                  onChange={(e) =>
                    handleInputChange(
                      "heart_rate",
                      parseInt(e.target.value) || undefined,
                    )
                  }
                  placeholder="e.g., 72"
                  min="30"
                  max="250"
                  style={errors.heart_rate ? errorInputStyle : inputStyle}
                />
                {errors.heart_rate && (
                  <div
                    style={{
                      color: "#ef4444",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {errors.heart_rate}
                  </div>
                )}
              </div>

              <div style={inputGroupStyle}>
                <label style={labelStyle} htmlFor="respiratory_rate">
                  Respiratory Rate (breaths/min)
                </label>
                <input
                  type="number"
                  id="respiratory_rate"
                  name="respiratory_rate"
                  value={formData.respiratory_rate || ""}
                  onChange={(e) =>
                    handleInputChange(
                      "respiratory_rate",
                      parseInt(e.target.value) || undefined,
                    )
                  }
                  placeholder="e.g., 16"
                  min="5"
                  max="60"
                  style={errors.respiratory_rate ? errorInputStyle : inputStyle}
                />
                {errors.respiratory_rate && (
                  <div
                    style={{
                      color: "#ef4444",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {errors.respiratory_rate}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Temperature & Oxygen Saturation */}
          <div style={sectionStyle}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                marginBottom: "16px",
                color: "#1f2937",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              🌡️ Temperature & Oxygen
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : isTablet
                    ? "1fr 1fr"
                    : "1fr 1fr",
                gap: "16px",
              }}
            >
              <div style={inputGroupStyle}>
                <label style={labelStyle} htmlFor="temperature">
                  Temperature (°C)
                </label>
                <input
                  type="number"
                  id="temperature"
                  name="temperature"
                  step="0.1"
                  value={formData.temperature || ""}
                  onChange={(e) =>
                    handleInputChange(
                      "temperature",
                      parseFloat(e.target.value) || undefined,
                    )
                  }
                  placeholder="e.g., 37.0"
                  min="30"
                  max="45"
                  style={errors.temperature ? errorInputStyle : inputStyle}
                />
                {errors.temperature && (
                  <div
                    style={{
                      color: "#ef4444",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {errors.temperature}
                  </div>
                )}
              </div>

              <div style={inputGroupStyle}>
                <label style={labelStyle} htmlFor="oxygen_saturation">
                  Oxygen Saturation (%)
                </label>
                <input
                  type="number"
                  id="oxygen_saturation"
                  name="oxygen_saturation"
                  value={formData.oxygen_saturation || ""}
                  onChange={(e) =>
                    handleInputChange(
                      "oxygen_saturation",
                      parseInt(e.target.value) || undefined,
                    )
                  }
                  placeholder="e.g., 98"
                  min="50"
                  max="100"
                  style={
                    errors.oxygen_saturation ? errorInputStyle : inputStyle
                  }
                />
                {errors.oxygen_saturation && (
                  <div
                    style={{
                      color: "#ef4444",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {errors.oxygen_saturation}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Weight & Height */}
          <div style={sectionStyle}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                marginBottom: "16px",
                color: "#1f2937",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              ⚖️ Weight & Height
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : isTablet
                    ? "1fr 1fr"
                    : "1fr 1fr",
                gap: "16px",
              }}
            >
              <div style={inputGroupStyle}>
                <label style={labelStyle} htmlFor="weight">
                  Weight (kg)
                </label>
                <input
                  type="number"
                  id="weight"
                  name="weight"
                  step="0.1"
                  value={formData.weight || ""}
                  onChange={(e) =>
                    handleInputChange(
                      "weight",
                      parseFloat(e.target.value) || undefined,
                    )
                  }
                  placeholder="e.g., 70.5"
                  min="0.5"
                  max="500"
                  style={errors.weight ? errorInputStyle : inputStyle}
                />
                {errors.weight && (
                  <div
                    style={{
                      color: "#ef4444",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {errors.weight}
                  </div>
                )}
              </div>

              <div style={inputGroupStyle}>
                <label style={labelStyle} htmlFor="height">
                  Height (cm)
                </label>
                <input
                  type="number"
                  id="height"
                  name="height"
                  step="0.1"
                  value={formData.height || ""}
                  onChange={(e) =>
                    handleInputChange(
                      "height",
                      parseFloat(e.target.value) || undefined,
                    )
                  }
                  placeholder="e.g., 175"
                  min="30"
                  max="250"
                  style={errors.height ? errorInputStyle : inputStyle}
                />
                {errors.height && (
                  <div
                    style={{
                      color: "#ef4444",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {errors.height}
                  </div>
                )}
              </div>
            </div>

            {/* BMI Display */}
            {formData.weight && formData.height && (
              <div
                style={{
                  marginTop: "12px",
                  padding: "8px 12px",
                  background: "#f0f9ff",
                  border: "1px solid #dbeafe",
                  borderRadius: "6px",
                  fontSize: "13px",
                  color: "#1e40af",
                }}
              >
                <strong>BMI:</strong>{" "}
                {(formData.weight / Math.pow(formData.height / 100, 2)).toFixed(
                  1,
                )}{" "}
                kg/m²
              </div>
            )}
          </div>

          {/* Pain Scale */}
          <div style={sectionStyle}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                marginBottom: "16px",
                color: "#1f2937",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              😣 Pain Assessment
            </h3>
            <div style={inputGroupStyle}>
              <label style={labelStyle} htmlFor="pain_scale">
                Pain Scale (0-10)
              </label>
              <select
                id="pain_scale"
                name="pain_scale"
                value={formData.pain_scale || ""}
                onChange={(e) =>
                  handleInputChange(
                    "pain_scale",
                    e.target.value ? parseInt(e.target.value) : undefined,
                  )
                }
                style={errors.pain_scale ? errorInputStyle : inputStyle}
              >
                <option value="">Select pain level...</option>
                <option value="0">0 - No pain</option>
                <option value="1">1 - Mild discomfort</option>
                <option value="2">2 - Mild pain</option>
                <option value="3">3 - Moderate pain</option>
                <option value="4">4 - Moderate pain</option>
                <option value="5">5 - Moderate pain</option>
                <option value="6">6 - Moderate to severe</option>
                <option value="7">7 - Severe pain</option>
                <option value="8">8 - Severe pain</option>
                <option value="9">9 - Very severe pain</option>
                <option value="10">10 - Worst possible pain</option>
              </select>
              {errors.pain_scale && (
                <div
                  style={{
                    color: "#ef4444",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {errors.pain_scale}
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          <div style={inputGroupStyle}>
            <label style={labelStyle} htmlFor="notes">
              Diagonosis
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes || ""}
              onChange={(e) => handleInputChange("notes", e.target.value)}
              placeholder="Diagonosis"
              rows={3}
              style={{ ...inputStyle, resize: "vertical", minHeight: "80px" }}
            />
          </div>

          {errors.submit && (
            <div
              style={{
                color: "#ef4444",
                fontSize: "14px",
                marginBottom: "16px",
                padding: "12px",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "6px",
              }}
            >
              {errors.submit}
            </div>
          )}

          {/* Footer */}
          <div style={footerStyle}>
            <div
              style={{
                fontSize: isMobile ? "13px" : "13px",
                color: "#6b7280",
                textAlign: "center",
              }}
            >
              {isEditMode ? "Editing" : "Recording"} vital signs for{" "}
              {patient.full_name ||
                `${patient.first_name} ${patient.last_name}`}
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                width: isMobile ? "100%" : "auto",
              }}
            >
              <button
                id="cancel_button"
                name="cancel_button"
                onClick={onClose}
                style={{
                  ...buttonStyle("secondary"),
                  flex: isMobile ? "1" : "none",
                }}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                id="save_button"
                name="save_button"
                onClick={handleSave}
                style={{
                  ...buttonStyle("primary"),
                  opacity: isSaving ? 0.7 : 1,
                  cursor: isSaving ? "not-allowed" : "pointer",
                  flex: isMobile ? "1" : "none",
                }}
                disabled={isSaving}
              >
                {isSaving
                  ? isEditMode
                    ? "💾 Updating..."
                    : "💾 Saving..."
                  : isEditMode
                    ? "💾 Update Vital Signs"
                    : "💾 Save Vital Signs"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          input:focus, select:focus, textarea:focus {
            border-color: #3b82f6 !important;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
          }
          
          button:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }
          
          @media (max-width: 768px) {
            .mobile-stack {
              flex-direction: column !important;
            }
            
            .mobile-full-width {
              width: 100% !important;
            }
            
            .mobile-grid-single {
              grid-template-columns: 1fr !important;
            }
            
            .mobile-text-left {
              text-align: left !important;
            }
          }
          
          @media (max-width: 480px) {
            .mobile-sm-stack {
              flex-direction: column !important;
            }
            
            .mobile-sm-center {
              justify-content: center !important;
            }
          }
          `}
      </style>
    </div>
  );
};

export default VitalSignsModal;
